import { ApiError } from "../../api";
import { platformLabel } from "../../platforms";
import { isDefinitiveRefusal } from "../shared/intent";
import { safeMessage, tidy } from "../shared/safe";
import type { Notice } from "./context";
import { effectiveDestinationState, type DeliveryView } from "./model";

// Turns the backend's lifecycle responses into honest view models. The rules this file encodes (CONTRACTS sections 9, 11, 12
// and A's publishing service):
//   - generation is not approval and approval is not publication, BUT the existing automatic mode can approve and queue a
//     passing article in one response, so what is said comes from the returned status and autoSubmission, never assumed;
//   - a publish response means "accepted for delivery" (queued:true, delivered:false). It never means delivered, and the
//     page never says "Published" because of it. Accepted work may still be waiting for the dispatcher to put it on the
//     queue (dispatch.pending): durable acceptance, not a failure and not delivery;
//   - positive wording needs a POSITIVE ACKNOWLEDGEMENT: an empty or malformed 200 is an uncertain answer that calls for a
//     status read, never "Approved" or "Accepted for delivery to 0 destinations";
//   - refusals (constraints, approval, zero destinations) are actionable; network failures, 5xx and unreadable answers are
//     uncertain and are retried only with the SAME Idempotency-Key;
//   - the delivery summary is its own thing, derived by the server from recorded jobs and immutable receipts.

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null && !Array.isArray(v);
const text = (v: unknown, max = 240): string | null => (typeof v === "string" && v !== "" ? safeMessage(v, max) : null);

export interface AcceptedDestination {
  platform: string;
  kind: string | null;
  jobId: string | null;
}
export interface FailedDestination {
  platform: string;
  error: string;
}
export interface SkippedDestination {
  platform: string;
  reason: string;
}
export interface Dispatch {
  sent: number;
  pending: number;
}

export interface PublishOutcome {
  /**
   * accepted:  the server durably accepted at least one destination (queued, NOT delivered).
   * refused:   the server definitely did not queue anything.
   * uncertain: it is not known whether anything was queued (dropped connection, 5xx, unreadable 200, key still in use).
   */
  kind: "accepted" | "refused" | "uncertain";
  headline: string;
  detail: string | null;
  accepted: AcceptedDestination[];
  failed: FailedDestination[];
  skipped: SkippedDestination[];
  errors: string[];
  note: string | null;
  code: string | null;
  /** Suggested next step, in words. */
  advice: string | null;
  /** True when the refusal means there is nowhere connected to publish to. */
  needsConnection: boolean;
  dispatch: Dispatch | null;
  /** The server recognised the Idempotency-Key and returned the original submission (nothing was queued twice). */
  replayed: boolean;
  submissionId: string | null;
}

const base = (): PublishOutcome => ({ kind: "refused", headline: "Publish failed.", detail: null, accepted: [], failed: [], skipped: [], errors: [], note: null, code: null, advice: null, needsConnection: false, dispatch: null, replayed: false, submissionId: null });

const readSkipped = (v: unknown): SkippedDestination[] =>
  (Array.isArray(v) ? v : []).filter(isObject).map((s) => ({ platform: tidy(s.platform, 60) || "unknown", reason: text(s.reason) ?? "No reason was given." }));
const readFailed = (v: unknown): FailedDestination[] =>
  (Array.isArray(v) ? v : []).filter(isObject).map((f) => ({ platform: tidy(f.platform, 60) || "unknown", error: text(f.error) ?? "The job could not be queued." }));
const readErrors = (v: unknown): string[] => (Array.isArray(v) ? v.filter((e): e is string => typeof e === "string").map((e) => safeMessage(e, 300)) : []);

function readDispatch(v: unknown): Dispatch | null {
  if (!isObject(v)) return null;
  const n = (x: unknown) => (typeof x === "number" && Number.isFinite(x) && x >= 0 ? Math.floor(x) : null);
  const sent = n(v.sent);
  const pending = n(v.pending);
  return sent === null || pending === null ? null : { sent, pending };
}

// ---- publish -------------------------------------------------------------------------------------------------

export interface PublishAck {
  accepted: AcceptedDestination[];
  failed: FailedDestination[];
  skipped: SkippedDestination[];
  dispatch: Dispatch | null;
  replayed: boolean;
  submissionId: string | null;
  note: string | null;
}

/**
 * A positive acknowledgement of a publish request: `queued === true`, not contradicted by `delivered: true`, and at least
 * one accepted destination. Destinations come from `destinations[]` (current servers) or the older parallel
 * `platforms[]`/`jobIds[]`. Anything else (an empty body, `queued` missing, zero destinations) is NOT an acknowledgement.
 */
export function parsePublishAck(body: unknown): PublishAck | null {
  if (!isObject(body) || body.queued !== true || body.delivered === true) return null;
  let accepted: AcceptedDestination[] = (Array.isArray(body.destinations) ? body.destinations : [])
    .filter(isObject)
    .filter((d) => typeof d.platform === "string" && d.platform !== "")
    .map((d) => ({ platform: tidy(d.platform, 60), kind: typeof d.kind === "string" ? d.kind : null, jobId: typeof d.jobId === "string" ? d.jobId : null }));
  if (accepted.length === 0 && Array.isArray(body.platforms)) {
    const ids = Array.isArray(body.jobIds) ? body.jobIds : [];
    accepted = body.platforms.filter((p): p is string => typeof p === "string" && p !== "").map((p, i) => ({ platform: tidy(p, 60), kind: null, jobId: typeof ids[i] === "string" ? (ids[i] as string) : null }));
  }
  if (accepted.length === 0) return null;
  return {
    accepted,
    failed: readFailed(body.failed),
    skipped: readSkipped(body.skipped),
    dispatch: readDispatch(body.dispatch),
    replayed: body.replayed === true,
    submissionId: typeof body.submissionId === "string" && body.submissionId !== "" ? body.submissionId : null,
    note: text(body.variantError, 400),
  };
}

/** What a positive publish acknowledgement means. Acceptance only: nothing here says "delivered" or "published". */
export function describePublishAck(ack: PublishAck): PublishOutcome {
  const n = ack.accepted.length;
  const partial = ack.failed.length > 0 || ack.skipped.length > 0;
  const pending = ack.dispatch?.pending ?? 0;
  const parts: string[] = [];
  if (ack.replayed) parts.push("Safe replay: the server recognised this request and returned the original submission. Nothing was queued a second time.");
  parts.push("Queued, not delivered yet. Each destination is delivered by its own job; results appear under Delivery and on the Queue page as they finish.");
  if (ack.dispatch && pending > 0) {
    parts.push(
      `${pending} of ${ack.dispatch.sent + pending} ${ack.dispatch.sent + pending === 1 ? "queue send is" : "queue sends are"} still pending: the work is durably accepted and the dispatcher will put it on the queue. This is not a failure, and it is not delivery.`,
    );
  }
  return {
    ...base(),
    kind: "accepted",
    headline: `${ack.replayed ? "Already accepted: " : "Accepted "}${ack.replayed ? "delivery to" : "for delivery to"} ${n} ${n === 1 ? "destination" : "destinations"}${partial ? ` (partial: ${ack.failed.length} could not be queued, ${ack.skipped.length} skipped)` : ""}.`,
    detail: parts.join(" "),
    accepted: ack.accepted,
    failed: ack.failed,
    skipped: ack.skipped,
    note: ack.note,
    advice: ack.failed.length > 0 ? "Destinations that could not be queued were not sent anything." : null,
    dispatch: ack.dispatch,
    replayed: ack.replayed,
    submissionId: ack.submissionId,
  };
}

/** The server answered 200 but not with an acknowledgement this page can read. Uncertain, never a success and never a refusal. */
export function publishUnreadable(): PublishOutcome {
  return {
    ...base(),
    kind: "uncertain",
    headline: "The server replied, but not with a confirmation this page recognises, so it is not known whether anything was queued.",
    advice: "The delivery record below shows what the server recorded. Retrying repeats the same request with the same key, so it cannot create a second publication.",
  };
}

/** A refused or failed publish. A 4xx is a definite refusal; a dropped connection, 5xx or "key still in use" is uncertain. */
export function describePublishRefusal(error: unknown): PublishOutcome {
  if (!(error instanceof ApiError)) {
    return {
      ...base(),
      kind: "uncertain",
      headline: "Couldn't reach the server, so it is not known whether anything was queued.",
      advice: "The delivery record below shows what the server recorded. Retrying repeats the same request with the same key, so it cannot create a second publication.",
    };
  }
  const b = error.body ?? {};
  const out: PublishOutcome = { ...base(), code: error.code ?? null, detail: safeMessage(error.message), skipped: readSkipped(b.skipped), failed: readFailed(b.failed), errors: readErrors(b.errors) };
  switch (error.code) {
    case "NO_ELIGIBLE_DESTINATIONS":
      return { ...out, headline: "Nothing was queued: there is no connected, usable destination.", needsConnection: true, advice: "Connect a cloud host or content platform for this client, or fix the reasons listed below, then publish again." };
    case "APPROVAL_REQUIRED":
      return { ...out, headline: "Not published: this content is not approved.", advice: "Approve it first. Publishing never skips approval." };
    case "CONSTRAINTS_FAILED":
      return { ...out, headline: "Not published: the content no longer passes the rules.", advice: "Client facts may have changed. Fix the listed problems (or the client's facts) and generate or approve again." };
    case "ENQUEUE_FAILED":
      // Servers before round 4 answered 502 when no queue send worked. Current servers accept durably instead.
      return { ...out, headline: "Nothing could be queued, so nothing will be delivered.", advice: "Try publishing again in a moment. If it keeps failing, the queue may be unavailable." };
    case "NOT_FOUND":
      return { ...out, headline: "This article no longer exists.", advice: "Refresh the page." };
    case "INVALID_IDEMPOTENCY_KEY":
      return { ...out, headline: "Nothing was queued: the request key was rejected.", advice: "Try again; a fresh key is generated for a new attempt." };
    case "IDEMPOTENCY_CONFLICT":
      return { ...out, headline: "Nothing was queued: the server says this request key was already used for a different request.", advice: "Publish again to start a new request." };
    case "IDEMPOTENCY_IN_PROGRESS":
      return {
        ...out,
        kind: "uncertain",
        headline: "An earlier request with the same key is still being processed, so its result is not known yet.",
        advice: "Nothing new was queued by this click. Wait a moment, then retry: the same request is replayed safely and returns the original result.",
      };
    default:
      if (error.status === 401 || error.status === 403) return { ...out, headline: "Not allowed: nothing was queued." };
      if (isDefinitiveRefusal(error)) return { ...out, headline: `Publish was refused (HTTP ${error.status}). Nothing was queued.` };
      return {
        ...out,
        kind: "uncertain",
        headline: `The server failed (HTTP ${error.status}), so it is not known whether anything was queued.`,
        advice: "The delivery record below shows what the server recorded. Retrying repeats the same request with the same key, so it cannot create a second publication.",
      };
  }
}

/** True when the publish request's intention is finished (accepted, or definitely refused) and its key can be dropped. */
export const publishIntentResolved = (o: PublishOutcome): boolean => o.kind !== "uncertain";

// ---- approve -------------------------------------------------------------------------------------------------

export interface ApproveOutcome {
  /** ok: approval confirmed. refused: definitely not approved. uncertain: not known, a status read decides. */
  kind: "ok" | "refused" | "uncertain";
  message: string;
  errors: string[];
  advice: string | null;
  code: string | null;
}

export interface ApproveAck {
  status: "approved" | "published";
  already: boolean;
}

/** `approved === true` and a status the server could only return for approved content. An empty or partial body is not one. */
export function parseApproveAck(body: unknown): ApproveAck | null {
  if (!isObject(body) || body.approved !== true) return null;
  if (body.status !== "approved" && body.status !== "published") return null;
  return { status: body.status, already: body.alreadyApproved === true };
}

export function describeApproveAck(ack: ApproveAck): ApproveOutcome {
  return {
    kind: "ok",
    code: null,
    errors: [],
    advice: null,
    message: ack.already
      ? `Already ${ack.status === "published" ? "published" : "approved"}. Nothing changed. Approval does not publish.`
      : "Approved. It has not been published: use Publish to send it to destinations.",
  };
}

export function approveUnreadable(): ApproveOutcome {
  return { kind: "uncertain", code: null, errors: [], advice: "Checking the article's status now.", message: "The server replied, but not with a confirmation this page recognises, so it is not known whether the article was approved." };
}

/** After an uncertain approval the article's own status settles it. */
export function describeApproveStatusRead(status: string | null): ApproveOutcome {
  if (status === "approved" || status === "published") {
    return { kind: "ok", code: null, errors: [], advice: null, message: `Confirmed by reading the article's status: it is ${status === "published" ? "published" : "approved"}. Approval does not publish.` };
  }
  if (status === "waiting_review" || status === "draft") {
    return { kind: "refused", code: null, errors: [], advice: "Approve again if you still want to.", message: "Not approved: the server did not confirm it (its reply was not a recognisable confirmation), and the article is still waiting for review." };
  }
  return { kind: "uncertain", code: null, errors: [], advice: "Refresh the page and check the article's status before trying again.", message: "It is still not known whether the article was approved." };
}

export function describeApproveRefusal(error: unknown): ApproveOutcome {
  if (!(error instanceof ApiError)) return { kind: "uncertain", code: null, errors: [], advice: "Checking the article's status now.", message: "Couldn't reach the server, so it is not known whether the content was approved." };
  const errors = readErrors(error.body?.errors);
  switch (error.code) {
    case "CONSTRAINTS_FAILED":
      return { kind: "refused", code: error.code, errors, message: "Not approved: the content fails the rules.", advice: "Fix the listed problems in the content or the client's facts, then generate again. There is no override." };
    case "NOT_APPROVABLE":
      return { kind: "refused", code: error.code, errors, message: safeMessage(error.message), advice: "Refresh: its state changed." };
    case "NOT_FOUND":
      return { kind: "refused", code: error.code, errors, message: "This article no longer exists.", advice: "Refresh the page." };
    default:
      if (error.status >= 500) return { kind: "uncertain", code: error.code ?? null, errors, message: `The server failed (HTTP ${error.status}), so it is not known whether the content was approved.`, advice: "Checking the article's status now." };
      return { kind: "refused", code: error.code ?? null, errors, message: error.status === 403 || error.status === 401 ? "Not allowed: it was not approved." : `Approval failed (HTTP ${error.status}): ${safeMessage(error.message)}`, advice: null };
  }
}

// ---- generate ------------------------------------------------------------------------------------------------

export interface GeneratedView {
  masterId: string;
  title: string;
  body: string;
  model: string;
  /** The article's stored status after generation: waiting_review, draft (rules failed) or approved (automatic mode). */
  status: string;
  constraintsPassed: boolean;
  errors: string[];
  warnings: string[];
  /** Automatic mode only. `queued` counts accepted destinations; nothing here says delivered. */
  autoSubmission: { ok: boolean; queued: number; code: string | null; message: string | null; skipped: SkippedDestination[] } | null;
}

/** Body of a 200 from POST /v1/content/generate. */
export function describeGenerated(body: unknown): GeneratedView | null {
  if (!isObject(body) || body.ok === false || typeof body.masterId !== "string" || body.masterId === "") return null;
  const c = isObject(body.constraints) ? body.constraints : {};
  const a = isObject(body.autoSubmission) ? body.autoSubmission : null;
  return {
    masterId: body.masterId,
    title: typeof body.title === "string" && body.title !== "" ? tidy(body.title, 300) : "(untitled)",
    body: typeof body.body === "string" ? body.body : "",
    model: typeof body.usedModel === "string" ? tidy(body.usedModel, 80) : "unknown",
    status: typeof body.status === "string" && body.status !== "" ? body.status : "unknown",
    // Only an explicit pass counts: a missing or malformed constraints object is not a pass.
    constraintsPassed: c.passed === true,
    errors: readErrors(c.errors),
    warnings: readErrors(c.warnings),
    autoSubmission: a
      ? { ok: a.ok === true, queued: typeof a.queued === "number" && Number.isFinite(a.queued) ? a.queued : 0, code: typeof a.code === "string" ? a.code : null, message: text(a.message, 300), skipped: readSkipped(a.skipped) }
      : null,
  };
}

export interface GenerationOutcome {
  notice: Notice;
  /** The one-line explanation under the preview title. */
  line: string;
  /** Approve is offered only for a passing article that is waiting for review. */
  canApprove: boolean;
  /** Automatic mode already accepted a submission: an ordinary first Publish must not be offered. */
  autoAccepted: boolean;
  /** The page should read the delivery record (something may have been queued and the response does not settle it). */
  readDelivery: boolean;
}

/**
 * What generation actually did, from the returned status and autoSubmission:
 *  - waiting_review / draft: a draft. Nothing approved, nothing queued (that is what those statuses mean);
 *  - approved + autoSubmission: the legacy automatic mode approved it and tried to submit it: say exactly what happened;
 *  - approved without autoSubmission, or any other status: say only what is known and read the delivery record.
 */
export function describeGeneration(g: GeneratedView): GenerationOutcome {
  const a = g.autoSubmission;
  if (!g.constraintsPassed && (g.status === "draft" || g.status === "waiting_review")) {
    return {
      notice: { tone: "warn", text: "Draft generated, but it fails the content rules and cannot be approved. Nothing has been approved, queued or published." },
      line: "Generated as a draft that fails the content rules. It cannot be approved.",
      canApprove: false,
      autoAccepted: false,
      readDelivery: false,
    };
  }
  if (g.status === "waiting_review" && g.constraintsPassed && (!a || (!a.ok && a.queued === 0))) {
    return {
      notice: { tone: "ok", text: "Draft generated. It is waiting for review: nothing has been approved, queued or published.", ...(a ? { detail: [a.message ?? "Review the article and approve a bounded campaign before publication.", ...(a.code ? [`Code: ${a.code}`] : [])] } : {}) },
      line: "Generating creates a draft only. Nothing has been approved, queued or published.",
      canApprove: true,
      autoAccepted: false,
      readDelivery: false,
    };
  }
  if (g.status === "approved" && a) {
    const skipped = a.skipped.map((s) => `Skipped: ${platformLabel(s.platform)}: ${s.reason}`);
    if (a.ok && a.queued > 0) {
      const n = a.queued;
      return {
        notice: {
          tone: skipped.length ? "warn" : "info",
          text: `Generated and approved automatically (this client is in automatic mode). Accepted for delivery to ${n} ${n === 1 ? "destination" : "destinations"}: queued, not delivered yet.`,
          detail: skipped,
        },
        line: "Approved automatically, and already submitted for delivery (queued, not delivered). Publishing it again would queue a second submission.",
        canApprove: false,
        autoAccepted: true,
        readDelivery: true,
      };
    }
    if (a.ok) {
      return {
        notice: { tone: "warn", text: "Generated and approved automatically. The server reported a submission but no accepted destination, so it is not known what was queued. The delivery record below shows what it recorded." },
        line: "Approved automatically. It is not known whether anything was submitted.",
        canApprove: false,
        autoAccepted: false,
        readDelivery: true,
      };
    }
    return {
      notice: {
        tone: "warn",
        text: `Generated and approved automatically, but automatic publishing did not queue anything${a.message ? `: ${a.message}` : "."}`,
        detail: [...(a.code ? [`Code: ${a.code}`] : []), ...skipped, "The article is approved. Use Publish when a destination is available."],
      },
      line: "Approved automatically. Automatic publishing did not queue anything.",
      canApprove: false,
      autoAccepted: false,
      readDelivery: false,
    };
  }
  if (g.status === "approved") {
    return {
      notice: { tone: "info", text: "Generated, and the article is already approved. The response does not say whether anything was submitted; the delivery record below shows what the server recorded." },
      line: "Already approved. Whether it was submitted is shown under Delivery.",
      canApprove: false,
      autoAccepted: false,
      readDelivery: true,
    };
  }
  return {
    notice: { tone: "info", text: `Generated. The server reports its status as "${tidy(g.status, 30)}".` },
    line: `Status: ${tidy(g.status, 30)}.`,
    canApprove: g.status === "waiting_review" && g.constraintsPassed,
    autoAccepted: false,
    readDelivery: g.status !== "draft",
  };
}

export function describeGenerateError(error: unknown): string {
  if (error instanceof ApiError) return error.status === 401 || error.status === 403 ? "Not allowed: nothing was generated." : `Generation failed: ${safeMessage(error.message)}`;
  return "Couldn't reach the server, so it is not known whether an article was generated. Check the article list before trying again.";
}

// ---- delivery ------------------------------------------------------------------------------------------------

export interface DeliveryDescription {
  label: string;
  tone: "ok" | "warn" | "bad" | "neutral";
  summary: string;
  /** Present when destinations need reconciliation (possible/confirmed unrecorded effect, or an uncertain destination). */
  reconciliationNote: string | null;
}

export function describeDelivery(d: DeliveryView): DeliveryDescription {
  const total = d.destinations.length;
  const of = `${d.confirmed} of ${total} ${total === 1 ? "destination" : "destinations"}`;
  const inFlight = d.destinations.filter((x) => x.inFlight).length;
  const flight = inFlight > 0 ? ` ${inFlight} ${inFlight === 1 ? "destination has" : "destinations have"} a newer attempt still in flight; the receipt shown there is from an earlier attempt.` : "";
  const waiting = d.destinations.filter((x) => x.dispatch === "pending").length;
  const dispatchNote = waiting > 0 ? ` ${waiting} ${waiting === 1 ? "attempt is" : "attempts are"} accepted but still waiting for the dispatcher to put ${waiting === 1 ? "it" : "them"} on the queue: not a failure, and not delivery.` : "";
  const reconciliationNote =
    d.needsReconciliation > 0
      ? `${d.needsReconciliation} ${d.needsReconciliation === 1 ? "destination needs" : "destinations need"} reconciliation: a possible or confirmed effect at the provider was not fully recorded, or the destination is blocked. Verify ${d.needsReconciliation === 1 ? "it" : "them"} there; Retry stays blocked until the outcome is reconciled, and the dashboard has no reconcile action yet.`
      : null;
  switch (d.state) {
    case "not_submitted":
      return { label: "Not submitted", tone: "neutral", summary: "Nothing has been sent for delivery.", reconciliationNote };
    case "queued":
      return { label: "Queued", tone: "neutral", summary: `Accepted for delivery; ${of} confirmed so far.${flight}${dispatchNote}`, reconciliationNote };
    case "partial":
      return { label: "Partly delivered", tone: "warn", summary: `${of} confirmed; the rest did not complete.${flight}${dispatchNote}`, reconciliationNote };
    case "delivered":
      return { label: "Delivered", tone: "ok", summary: `Every submitted destination holds a recorded delivery (${of}).${flight}${dispatchNote}`, reconciliationNote };
    case "failed":
      return { label: d.needsReconciliation > 0 ? "Failed, outcome unknown" : "Failed", tone: "bad", summary: `None of ${total === 1 ? "the 1 destination" : `the ${total} destinations`} holds a recorded delivery.`, reconciliationNote };
    default:
      return { label: "Unknown", tone: "neutral", summary: "The server returned a delivery state this page does not recognise.", reconciliationNote };
  }
}

export interface DestinationDescription {
  label: string;
  tone: "ok" | "warn" | "bad" | "neutral";
  /** Plain-words explanation of what the state means for this destination. */
  note: string | null;
}

/** The destination's CURRENT state, from the server's own derivation (see effectiveDestinationState for older servers). */
export function describeDestination(d: DeliveryView["destinations"][number]): DestinationDescription {
  const state = effectiveDestinationState(d);
  if (d.attributionConflict && state === "unknown") {
    return { label: "Not confirmed for this account", tone: "warn", note: "The only receipt for this destination belongs to a different account or credential revision. It is listed as history and is not this account's delivery." };
  }
  switch (state) {
    case "delivered":
      return d.inFlight
        ? { label: "Delivered, newer attempt in flight", tone: "warn", note: `An earlier attempt holds a recorded delivery. A newer attempt is ${d.dispatch === "pending" ? "accepted but still waiting for the dispatcher" : "queued or running"} and has not finished.` }
        : { label: "Delivered", tone: "ok", note: null };
    case "queued":
      if (d.dispatch === "pending") return { label: "Queued, waiting for the dispatcher", tone: "neutral", note: "Accepted and recorded, but the queue send is still pending: the dispatcher will put it on the queue. Not a failure, and not delivered." };
      return { label: "Queued", tone: "neutral", note: d.jobStatus === "failed" ? "The queue is retrying this attempt automatically." : null };
    case "failed":
      return { label: "Failed", tone: "bad", note: "No effect at the provider is recorded for the latest attempt." };
    case "needs_reconciliation":
      return {
        label: "Needs reconciliation",
        tone: "warn",
        note: d.destinationState === "uncertain" ? "This destination is blocked: an earlier sender may have changed it and that cannot be ruled out." : "The provider may already have acted; the result was not fully recorded.",
      };
    default:
      return { label: "Unknown", tone: "neutral", note: "The server returned a state this page does not recognise." };
  }
}

export function destinationLabel(platform: string): string {
  return platformLabel(platform);
}
